import {
  EV_COMMAND_STATUS_LABELS,
  EV_COMMAND_TYPE_LABELS,
  EV_OPERATION_MODEL_LABELS,
  type EvChargerDetail,
  type EvCommand,
  type EvOperatorCommandType,
  rooms,
} from "@station/contracts";
import { Alert, Button, Card, EmptyState, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { KeyRound, ListChecks, Play, ShieldOff, Terminal } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import {
  type ChargerRef,
  CommandDialog,
  CredentialsDialog,
  DisableCredentialsDialog,
  RemoteStartDialog,
} from "../../../../features/ev/charger-dialogs.js";
import { CommandsTable } from "../../../../features/ev/command-table.js";
import { formatKw, formatKwh, formatWatts } from "../../../../features/ev/ev-values.js";
import { useEvCharger, useEvCommands, useEvPermissions } from "../../../../features/ev/queries.js";
import {
  ConnectorStatusBadge,
  CredentialBadge,
  evCrumb,
  EvPage,
  Field,
  OnlineBadge,
  SessionPhaseBadge,
  TableFrame,
  Th,
} from "../../../../features/ev/shared.js";
import { formatDuration } from "../../../../features/wash/wash-values.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../../lib/format.js";
import { useRoom } from "../../../../lib/realtime-context.js";

const searchSchema = z.object({ station: z.string().uuid().optional().catch(undefined) });

export const Route = createFileRoute("/_app/sarj/uniteler/$id")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ChargerRoute,
});

/**
 * Şarj ünitesi ayrıntısı (Prompt 19 web, `GET /ev/chargers/{id}`): OCPP durumu ve cihaz bilgisi, kimlik bilgisi (üret /
 * döndür — parola bir kez gösterilir — ve devre dışı bırak, `ev.manage`), konnektörler, canlı oturumlar, komutlar (uzaktan
 * başlat ve işletim komutları `ev.operate`; yapılandırma değişikliği / yazılım / tanılama `ev.maintenance.manage` + adım
 * yükseltme), son 20 bağlantı aralığı ve son okunan yapılandırma. Komut sonuçlanana dek ekran kısa aralıkla yoklanır.
 */
function ChargerRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const search = Route.useSearch();
  const permissions = useEvPermissions();
  const charger = useEvCharger(id);
  useRoom(charger.data ? rooms.station(charger.data.stationId) : null);
  const [dialog, setDialog] = useState<
    "start" | "command" | "configuration" | "credentials" | "disable" | null
  >(null);
  const data = charger.data;
  const ref: ChargerRef | null = data
    ? {
        id: data.id,
        name: data.name,
        ocppIdentity: data.ocppIdentity,
        connectors: data.connectors.map((connector) => ({
          connectorNo: connector.connectorNo,
          connectorType: connector.connectorType,
          powerKw: connector.powerKw,
          ocppStatus: connector.ocppStatus,
        })),
      }
    : null;
  const own = data?.operationModel === "OWN_CSMS";
  const canStart = Boolean(data && permissions.canOperate && data.managed && data.ocpp.connected);
  const canCommand = Boolean(data && data.managed && (permissions.canOperate || permissions.canMaintain));
  const canCredentials = Boolean(
    data && permissions.canManage && own && data.ocppIdentity && data.status !== "DECOMMISSIONED",
  );
  const canDisable = Boolean(data && permissions.canManage && data.credential.enabled);

  return (
    <EvPage
      title={data ? data.name : t("menu.evChargers")}
      description={data ? `${data.stationCode} ${data.stationName}` : undefined}
      breadcrumb={[
        evCrumb(t("menu.evChargers"), "/sarj/uniteler", search.station ? { station: search.station } : {}),
        { label: data?.name ?? "…" },
      ]}
      actions={
        data ? (
          <div className="flex flex-wrap gap-2">
            {canStart ? (
              <Button onClick={() => setDialog("start")}>
                <Play aria-hidden="true" />
                {t("ev.remoteStart.open")}
              </Button>
            ) : null}
            {canCommand ? (
              <Button variant="outline" onClick={() => setDialog("command")}>
                <Terminal aria-hidden="true" />
                {t("ev.commands.open")}
              </Button>
            ) : null}
            {canCredentials ? (
              <Button variant="outline" onClick={() => setDialog("credentials")}>
                <KeyRound aria-hidden="true" />
                {data.credential.configured ? t("ev.credentials.rotate") : t("ev.credentials.generate")}
              </Button>
            ) : null}
            {canDisable ? (
              <Button variant="ghost" onClick={() => setDialog("disable")}>
                <ShieldOff aria-hidden="true" />
                {t("ev.credentials.disable")}
              </Button>
            ) : null}
          </div>
        ) : null
      }
    >
      {charger.error ? (
        <Alert tone="danger">{errorMessage(charger.error)}</Alert>
      ) : !data || !ref ? (
        <SkeletonRows rows={8} />
      ) : (
        <>
          <ChargerBody
            charger={data}
            canReadConfiguration={permissions.canOperate && data.managed}
            onReadConfiguration={() => setDialog("configuration")}
          />
          {dialog === "start" ? (
            <RemoteStartDialog open onOpenChange={(open) => !open && setDialog(null)} charger={ref} />
          ) : null}
          {dialog === "command" || dialog === "configuration" ? (
            <CommandDialog
              open
              onOpenChange={(open) => !open && setDialog(null)}
              charger={ref}
              canOperate={permissions.canOperate}
              canMaintain={permissions.canMaintain}
              defaultType={
                dialog === "configuration" ? ("GetConfiguration" as EvOperatorCommandType) : undefined
              }
            />
          ) : null}
          <CredentialsDialog
            open={dialog === "credentials"}
            onOpenChange={(open) => !open && setDialog(null)}
            charger={ref}
            configured={data.credential.configured}
          />
          <DisableCredentialsDialog
            open={dialog === "disable"}
            onOpenChange={(open) => !open && setDialog(null)}
            charger={ref}
          />
        </>
      )}
    </EvPage>
  );
}

function ChargerBody({
  charger,
  canReadConfiguration,
  onReadConfiguration,
}: {
  charger: EvChargerDetail;
  canReadConfiguration: boolean;
  onReadConfiguration: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const counts = Object.entries(charger.commandCounts).filter(([, count]) => count > 0);
  return (
    <>
      {!charger.managed ? (
        <Alert tone="info">
          {charger.operationModel === "NETWORK_PARTNER"
            ? t("ev.chargers.partnerNote")
            : t("ev.chargers.unmanagedNote")}
        </Alert>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="grid content-start gap-3 p-4">
          <h2 className="text-base font-semibold">{t("ev.chargers.connectionTitle")}</h2>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label={t("ev.fields.ocppIdentity")}>
              <span className="font-mono">{charger.ocppIdentity ?? "—"}</span>
            </Field>
            <Field label={t("ev.chargers.connection")}>
              {charger.managed ? (
                <OnlineBadge online={charger.ocpp.connected} />
              ) : (
                <StatusBadge tone="neutral">{t("ev.chargers.notManaged")}</StatusBadge>
              )}
            </Field>
            <Field label={t("ev.chargers.connectedSince")}>
              {formatDateTime(charger.ocpp.connectedSince)}
            </Field>
            <Field label={t("ev.chargers.lastSeenLabel")}>{formatDateTime(charger.ocpp.lastSeenAt)}</Field>
            <Field label={t("ev.chargers.lastBoot")}>{formatDateTime(charger.ocpp.lastBootAt)}</Field>
            <Field label={t("ev.chargers.model")}>{EV_OPERATION_MODEL_LABELS[charger.operationModel]}</Field>
            <Field label={t("ev.chargers.credentialLabel")}>
              <span className="grid justify-items-start gap-1">
                <CredentialBadge charger={charger} />
                {charger.credential.rotatedAt ? (
                  <span className="text-xs font-normal text-muted-foreground">
                    {t("ev.chargers.rotatedAt", { at: formatDateTime(charger.credential.rotatedAt) })}
                  </span>
                ) : null}
              </span>
            </Field>
            <Field label={t("ev.credentials.securityProfile")}>
              {charger.credential.securityProfile === null
                ? "—"
                : t("ev.chargers.profileNo", { no: charger.credential.securityProfile })}
            </Field>
          </dl>
        </Card>
        <Card className="grid content-start gap-3 p-4">
          <h2 className="text-base font-semibold">{t("ev.chargers.deviceTitle")}</h2>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Field label={t("ev.chargers.vendor")}>{charger.ocpp.vendor ?? charger.brand ?? "—"}</Field>
            <Field label={t("ev.chargers.modelName")}>
              {charger.ocpp.model ?? charger.assetModel ?? "—"}
            </Field>
            <Field label={t("ev.chargers.serialNo")}>
              <span className="font-mono">{charger.ocpp.serialNo ?? "—"}</span>
            </Field>
            <Field label={t("ev.chargers.firmwareVersion")}>{charger.ocpp.firmwareVersion ?? "—"}</Field>
            <Field label={t("ev.chargers.firmwareStatus")}>{charger.ocpp.firmwareStatus ?? "—"}</Field>
            <Field label={t("ev.chargers.diagnosticsStatus")}>{charger.ocpp.diagnosticsStatus ?? "—"}</Field>
            <Field label={t("ev.chargers.maxPowerLabel")}>{formatKw(charger.maxPowerKw)}</Field>
            <Field label={t("ev.chargers.assetStatus")}>{t(`enums.assetStatus.${charger.status}`)}</Field>
          </dl>
        </Card>
      </div>

      <TableFrame label={t("ev.chargers.connectorsTitle")}>
        <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
          <tr>
            <Th>{t("ev.fields.connector")}</Th>
            <Th>{t("ev.fields.connectorType")}</Th>
            <Th align="right">{t("ev.fields.power")}</Th>
            <Th>{t("ev.chargers.assetStatus")}</Th>
            <Th>{t("ev.chargers.ocppStatus")}</Th>
            <Th>{t("ev.chargers.statusSince")}</Th>
          </tr>
        </thead>
        <tbody>
          {charger.connectors.map((connector) => (
            <tr key={connector.connectorNo} className="border-t">
              <th scope="row" className="px-3 py-2 text-left font-medium">
                {t("ev.connector.no", { no: connector.connectorNo })}
              </th>
              <td className="px-3 py-2">
                {t(`enums.connectorType.${connector.connectorType}`)} · {connector.currentType}
              </td>
              <td className="px-3 py-2 text-right tabular">{formatKw(connector.powerKw)}</td>
              <td className="px-3 py-2">{t(`enums.assetStatus.${connector.assetStatus}`)}</td>
              <td className="px-3 py-2">
                <ConnectorStatusBadge status={connector.ocppStatus} errorCode={connector.errorCode} />
              </td>
              <td className="px-3 py-2 tabular whitespace-nowrap">{formatDateTime(connector.statusSince)}</td>
            </tr>
          ))}
        </tbody>
      </TableFrame>

      {charger.liveSessions.length > 0 ? (
        <Card className="grid gap-3 p-4" data-testid="ev-charger-live-sessions">
          <h2 className="text-base font-semibold">{t("ev.chargers.liveSessions")}</h2>
          <ul className="grid gap-2">
            {charger.liveSessions.map((session) => (
              <li key={session.id} className="flex flex-wrap items-center gap-2 text-sm">
                <SessionPhaseBadge phase={session.phase} />
                <span>{t("ev.connector.no", { no: session.connectorNo ?? "—" })}</span>
                <span className="tabular">{formatKwh(session.energyKwh)}</span>
                <span className="tabular">{formatWatts(session.lastPowerW)}</span>
                <Link
                  to="/sarj/oturumlar/$id"
                  params={{ id: session.id }}
                  search={{ station: charger.stationId } as never}
                  className="underline underline-offset-4"
                >
                  {t("ev.dashboard.openSession")}
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <CommandsCard charger={charger} counts={counts} />

      <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0">
        <h2 className="text-base font-semibold">{t("ev.chargers.connectionsTitle")}</h2>
        {charger.connections.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ev.chargers.noConnections")}</p>
        ) : (
          <TableFrame label={t("ev.chargers.connectionsTitle")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("ev.chargers.connectedAt")}</Th>
                <Th>{t("ev.chargers.disconnectedAt")}</Th>
                <Th align="right">{t("ev.fields.duration")}</Th>
                <Th>{t("ev.chargers.closeReasonLabel")}</Th>
              </tr>
            </thead>
            <tbody>
              {charger.connections.map((connection) => (
                <tr key={connection.connectedAt} className="border-t">
                  <td className="px-3 py-2 tabular whitespace-nowrap">
                    {formatDateTime(connection.connectedAt)}
                  </td>
                  <td className="px-3 py-2 tabular whitespace-nowrap">
                    {connection.disconnectedAt ? (
                      formatDateTime(connection.disconnectedAt)
                    ) : (
                      <StatusBadge tone="success">{t("ev.chargers.stillConnected")}</StatusBadge>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right tabular">
                    {formatDuration(connection.durationSeconds)}
                  </td>
                  <td className="px-3 py-2">
                    {connection.closeReason ? t(`ev.closeReason.${connection.closeReason}`) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Card>

      <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0" data-testid="ev-configuration">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="grid gap-0.5">
            <h2 className="text-base font-semibold">{t("ev.chargers.configurationTitle")}</h2>
            <p className="text-xs text-muted-foreground">
              {charger.configurationReadAt
                ? t("ev.chargers.configurationReadAt", { at: formatDateTime(charger.configurationReadAt) })
                : t("ev.chargers.configurationNever")}
            </p>
          </div>
          {canReadConfiguration ? (
            <Button size="sm" variant="outline" onClick={onReadConfiguration}>
              <ListChecks aria-hidden="true" />
              {EV_COMMAND_TYPE_LABELS.GetConfiguration}
            </Button>
          ) : null}
        </div>
        {charger.configuration.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("ev.chargers.configurationEmpty")}</p>
        ) : (
          <TableFrame label={t("ev.chargers.configurationTitle")}>
            <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
              <tr>
                <Th>{t("ev.commands.key")}</Th>
                <Th>{t("ev.commands.value")}</Th>
                <Th>{t("ev.chargers.readonly")}</Th>
              </tr>
            </thead>
            <tbody>
              {charger.configuration.map((item) => (
                <tr key={item.key} className="border-t">
                  <th scope="row" className="px-3 py-1.5 text-left font-mono text-xs font-medium">
                    {item.key}
                  </th>
                  <td className="max-w-md break-all px-3 py-1.5 font-mono text-xs">{item.value ?? "—"}</td>
                  <td className="px-3 py-1.5 text-xs">{item.readonly ? t("common.yes") : t("common.no")}</td>
                </tr>
              ))}
            </tbody>
          </TableFrame>
        )}
      </Card>
    </>
  );
}

/** Komut listesi (imleçli): tür, yük özeti, kaynak, durum, yanıt, ilerleme (yazılım / tanılama) ve zamanlar. */
function CommandsCard({
  charger,
  counts,
}: {
  charger: EvChargerDetail;
  counts: Array<[string, number]>;
}): ReactElement {
  const { t } = useTranslation();
  const pages = useCursorPages(charger.id);
  const commands = useEvCommands(charger.id, pages.cursor);
  const items: EvCommand[] = commands.data?.items ?? [];
  return (
    <Card className="grid min-w-0 gap-3 p-4 [&>*]:min-w-0" data-testid="ev-commands">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{t("ev.commands.listTitle")}</h2>
        {counts.length > 0 ? (
          <span className="flex flex-wrap gap-1.5 text-xs text-muted-foreground">
            {t("ev.commands.last30")}
            {counts.map(([status, count]) => (
              <span key={status}>
                {(EV_COMMAND_STATUS_LABELS as Record<string, string>)[status] ?? status}: {formatCount(count)}
              </span>
            ))}
          </span>
        ) : null}
      </div>
      {commands.error ? (
        <Alert tone="danger">{errorMessage(commands.error)}</Alert>
      ) : !commands.data ? (
        <SkeletonRows rows={4} />
      ) : items.length === 0 ? (
        <EmptyState
          headingLevel={3}
          icon={<Terminal />}
          title={t("ev.commands.emptyTitle")}
          description={t("ev.commands.emptyDescription")}
        />
      ) : (
        <>
          <CommandsTable commands={items} label={t("ev.commands.listTitle")} />
          {pages.hasPrevious || commands.data.page.hasMore ? (
            <div className="flex items-center justify-end gap-2 text-sm">
              <span className="text-muted-foreground">
                {t("common.pageNumber", { page: pages.pageNumber })}
              </span>
              <Button size="sm" variant="outline" disabled={!pages.hasPrevious} onClick={pages.previous}>
                {t("common.back")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!commands.data.page.hasMore}
                onClick={() => pages.next(commands.data?.page.nextCursor)}
              >
                {t("common.next")}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
